import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { merge } from 'lodash'
import { z } from 'zod'
import api from '../../actions/api'
import { decodeJwt } from 'jose'
import moment from 'moment'
import localforage from 'localforage'

localforage.config({
  driver: localforage.INDEXEDDB,
})

/**
 * This schema defines the shape of a certificate object.
 * It is used to validate the data returned by the API before storing it.
 */
const certificateSchema = z.object({
  certificateId: z.string().min(1, 'Certificate ID can\'t be an empty string.'),
  certificateValidUntil: z.coerce.date(),
  removalTypes: z.array(z.string().min(1), { 
    invalid_type_error: 'Removal types must be an array of strings.', 
    required_error: 'Removal types are required for a valid certificate.' 
  }),
  locationIds: z.array(z.coerce.number().int().min(0), {
    invalid_type_error: 'Location IDs must be an array of numbers.',
    required_error: 'Location IDs are required for a valid certificate.'
  }),
  companyName: z.string().min(1, 'Company name can\'t be an empty string.').nullable().optional(),
  workerName: z.string().min(1, 'Worker name can\'t be an empty string.').nullable().optional(),
})

/**
 * @typedef {Object} Certificate
 * @property {string} certificateId - The unique identifier of the certificate.
 * @property {Date} certificateValidUntil - The date until which the certificate is valid.
 * @property {Array<string>} removalTypes - The types of removals the certificate is valid for.
 * @property {Array<number>} locationIds - The IDs of the locations the certificate is valid for.
 * @property {string} [companyName] - The name of the company that issued the certificate.
 */

/**
 * @typedef {Object} CertificateStore
 * @property {Array<Certificate>} certificates - The array of all fetched certificates.
 * @property {() => Promise<void>} fetchAllCertificates - Fetches all certificates from the server and stores them in the state.
 */

/**
 * This store manages the client-side state of the different certificates.
 * It is used in the `CertificateCheckModal` component to provide select options 
 * and validation for the certificate number input.
 * 
 * @returns {CertificateStore} The store object containing certificates and fetch function.
 */
export const useCertificateStore = create()(
  persist(
    immer(
      (set, get) => ({

        /**
         * This array holds all fetched certificates from the server,
         * as encoded strings. When a certificate is needed, it can be
         * decoded and validated using the `decodeCertificateData` and
         * `checkCertificateJWT` functions respectively.
         * 
         * @type {Array<{ certificateId: string, encodedData: string }>}
         */
        certificates: [],

        lastSync: null,

        fetchAllCertificates: async () => {
          
          if(!window.navigator.onLine) {
            console.warn('No internet connection. Could not fetch certificates.')
            return;
          }

          try {
            const { data } = await window.axios.get(api.certificates.all)

            const parsed = await z.array(z.object({
              certificateId: z.string(),
              encodedData: z.string()
            })).spa(data);

            if(!parsed.success) {
              console.error('Could not parse server data:', parsed.error)
              return;
            }

            set(state => {
              state.certificates = parsed.data;
              state.lastSync = moment().unix();
            })

            return;
          } 
          catch(err) {
            console.error('Could not fetch certificates:', err)
            return;
          }
        },

        /**
         * This function decodes the certificate data from a JWT string.
         * It returns the decoded certificate data as an object. When the
         * data of the certificate is needed, this function can be used to
         * decode the JWT string on the client side.
         * 
         * @param {string} jwt - The encoded certificate data to decode.
         * @returns {Promise<Certificate>} - The decoded certificate data.
         * @throws {Error} - If the JWT could not be decoded.
         */
        decodeCertificateData: (jwt) => {
          try {
            
            const decoded = decodeJwt(jwt)
            const parsed = certificateSchema.safeParse(decoded)

            if(!parsed.success) {
              console.warn('Could not validate certificate data:', parsed.error.format())
              return { data: null, error: parsed.error }
            }

            return { data: parsed.data, error: null }
          }
          catch (err) {
            console.warn('Could not decode certificate data:', err)
            return { data: null, error: err }
          }
        },

      })
    ),
    {
      name: 'bsp:certificate-store',
      storage: createJSONStorage(() => ({
        getItem: async (name) => {
          return await localforage.getItem(name);
        },
        setItem: async (name, value) => {
          return await localforage.setItem(name, value);
        },
        removeItem: async (name) => {
          return await localforage.removeItem(name);
        },
      })),
      partialize: (state) => ({ 
        certificates: state.certificates,
        lastSync: state.lastSync
      }),
      merge: (persisted, current) => merge(current, persisted)
    }
  )
)